import {
    CreateUser,
    isOfTypeUser,
    Permission,
    permissionTranslation,
    User,
} from '@/models/user';
import { FunctionComponent, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { set } from 'lodash';
import { DrawerActions } from '@/components/Drawer/DrawerActions';
import { LoadingButton } from '@mui/lab';
import { usePostUserMutation, usePutUserMutation } from '@/api/v2/user';
import { useNotification } from '@starjack/features/notifications';

type UserDrawerContentProps = {
    user: User | CreateUser;
    onClose: () => void;
};

export const UserDrawerContent: FunctionComponent<UserDrawerContentProps> = ({
    user,
    onClose,
}) => {
    const [localUser, setLocalUser] = useState<User | CreateUser>(user);
    const [putUser, { isLoading: putLoading }] = usePutUserMutation();
    const [postUser, { isLoading: postLoading }] = usePostUserMutation();
    const { addNotification } = useNotification();

    useEffect(() => {
        setLocalUser(user);
    }, [user]);

    const onChangeHandler = (key: string) => (e) => {
        const { value } = e.target;

        setLocalUser((prevLocalWebshop) => {
            const copy = { ...prevLocalWebshop };

            set(copy, key, value);
            return copy;
        });
    };

    const onCheckboxChange = (permission: Permission) => (e) => {
        const { checked } = e.target;

        const copyPermissions = [...localUser.permissions];
        if (checked) {
            copyPermissions.push(permission);
        } else {
            const pIndex = copyPermissions.indexOf(permission);

            if (pIndex > -1) {
                copyPermissions.splice(pIndex, 1);
            }
        }

        setLocalUser((prevUser) => ({
            ...prevUser,
            permissions: [...copyPermissions],
        }));
    };

    const onCreateOrUpdate = () => {
        const mutationHandler = isOfTypeUser(localUser) ? putUser : postUser;

        mutationHandler(localUser)
            .unwrap()
            .then((webshop) => {
                addNotification({
                    message: 'Speichern erfolgreich',
                    type: 'success',
                });

                setLocalUser(webshop);
                onClose();
            })
            .catch((reason) => {
                console.error(reason);

                let message =
                    'Fehler beim Speichern. Versuchen Sie es später noch einmal';
                if (
                    reason?.data.errors[0]?.error === 'username_already_exists'
                ) {
                    message =
                        'Ein User mit diesem Benutzername existiert bereits. Bitte wählen Sie einen anderen';
                }

                addNotification({
                    message,
                    type: 'error',
                });
            });
    };

    return (
        <Box
            sx={{
                width: '480px',
            }}
        >
            <Box
                sx={(theme) => ({
                    backgroundColor: theme.palette.primary.dark,
                    color: '#fff',
                    p: 4,
                })}
            >
                <Typography variant='h6'>
                    {isOfTypeUser(localUser)
                        ? 'Benutzer bearbeiten'
                        : 'Neuen Benutzer erstellen'}
                </Typography>
            </Box>

            <Stack
                spacing={3}
                sx={{
                    p: 4,
                }}
            >
                <Grid spacing={3} container>
                    <Grid md={12} item>
                        <TextField
                            value={localUser.username}
                            onChange={onChangeHandler('username')}
                            label='Username'
                            sx={{
                                width: '100%',
                            }}
                        />
                    </Grid>

                    <Grid md={12} item>
                        <TextField
                            onChange={onChangeHandler('password')}
                            type='password'
                            label='Passwort'
                            sx={{
                                width: '100%',
                            }}
                        />
                    </Grid>

                    <Grid md={12} item>
                        <Typography
                            sx={{
                                fontWeight: 500,
                                fontSize: '1.2rem',
                                mb: 3,
                            }}
                        >
                            Seiten für Benutzer freigeben:
                        </Typography>
                        <Grid spacing={3} container>
                            {Object.keys(permissionTranslation).map(
                                (permissionKey: Permission) => {
                                    // this is just needed for
                                    if (permissionKey === Permission.ADMIN)
                                        return null;

                                    const checked =
                                        localUser.permissions.includes(
                                            permissionKey
                                        );

                                    return (
                                        <Grid key={permissionKey} md={6} item>
                                            <FormControlLabel
                                                label={
                                                    permissionTranslation[
                                                        permissionKey
                                                    ]
                                                }
                                                control={
                                                    <Checkbox
                                                        checked={checked}
                                                        onChange={onCheckboxChange(
                                                            permissionKey
                                                        )}
                                                    />
                                                }
                                            />
                                        </Grid>
                                    );
                                }
                            )}
                        </Grid>
                    </Grid>
                </Grid>

                <DrawerActions>
                    <Stack
                        sx={{
                            alignItems: 'center',
                        }}
                        spacing={2}
                        direction='row'
                    >
                        <Button variant='outlined' onClick={onClose}>
                            Abbrechen
                        </Button>

                        <LoadingButton
                            variant='contained'
                            onClick={onCreateOrUpdate}
                            loading={putLoading || postLoading}
                            disabled={
                                isOfTypeUser(localUser)
                                    ? !localUser.username
                                    : !(
                                          localUser.username &&
                                          localUser.password
                                      )
                            }
                        >
                            Speichern
                        </LoadingButton>
                    </Stack>
                </DrawerActions>
            </Stack>
        </Box>
    );
};
